import React, { useState, useEffect, KeyboardEvent, useRef } from "react";
import NavBar from "../components/NavBar";
import ForegroundColorSet from "../../utilities/enums/ForegroundColorSet";
import { ReactComponent as SortIcon } from "../../assets/icons/sort-filter.svg?react";
import { ReactComponent as SaveIcon } from "../../assets/icons/save-icon.svg?react";
import { ReactComponent as CompleteIcon } from "../../assets/icons/checkbox-checked-regular.svg?react";
import { ReactComponent as BackIcon } from "../../assets/icons/back-chevron.svg?react";
import "./StockCheck.css";
import AbstractButton from "../components/AbstractButton";
import BackgroundColorSet from "../../utilities/enums/BackgroundColorSet";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import StockCheckExtractSearch, {
  defaultStockCheckExtractSearch,
  StockSearchOrderOption,
} from "../../interfaces/SceSearch";
import { OrderDirection } from "../../interfaces/PartSearch";
import { apiFetch } from "../../utilities/AuthenticatedFetch";
import ScrollableCardList from "../components/ScrollableCardList";
import { StockCheckExtract } from "../../interfaces/Sce";
import StockCheckCard from "./StockCheckCard";
import { useNavigate } from "react-router-dom";
import StockSortBy from "./StockSortBy";
import { StockSliceSavedCheck } from "../../store/slices/stockcheckslice";
import StockLargeWarning from "./StockLargeWarning";

const StockCheck = () => {
  const depotId = useSelector(
    (state: RootState) => state.root.auth.currentDepotId
  );

  const currentCompanyId = useSelector(
    (state: RootState) => state.root.auth.companyId
  );

  const URL = "url";
  const currentUrl = localStorage.getItem(URL) ?? "";

  const [searchResults, setSearchResults] = useState<StockCheckExtract[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [range, setRange] = useState({ start: 0, end: 10 });
  const [searchInputTerm, setSearchInputTerm] = useState("");
  const systemSavedAllChecks = useSelector(
    (state: RootState) => state.root.stock.savedChecks
  );
  const navigate = useNavigate();

  const SEARCH_ENDPOINT = "/partsapp/stock/search";
  const searchParams = defaultStockCheckExtractSearch;

  const [searchAndFilterMenuVisible, setSearchAndFilterMenuVisible] =
    useState(false);
  const [showSearchAndFilterMenuTrigger, setShowSearchAndFilterMenuTrigger] =
    useState(0);

  const [stockLargeWarningVisible, setStockLargeWarningVisible] =
    useState(false);
  const largeWarningStockCheckPlaceholder = useRef<StockCheckExtract | null>(null);

  const SearchAndFilterClasses = searchAndFilterMenuVisible
    ? "modal-open"
    : "modal-closed";

  const updateSort = (data: StockCheckExtractSearch) => {
    setSearchAndFilterMenuVisible(false);
    setSearchResults([]);
    const response = fetchSearchResults(
      0,
      range.end,
      data.OrderDirection,
      data.Order
    );
    response.then((e) => {
      setSearchResults(e);
    });
  };
  const showSearchAndFilterMenu = () => {
    setShowSearchAndFilterMenuTrigger(showSearchAndFilterMenuTrigger + 1);
    setSearchAndFilterMenuVisible(!searchAndFilterMenuVisible);
  };
  const cancelFilterButtonClicked = () => {
    setSearchAndFilterMenuVisible(false);
  };
  async function fetchSearchResults(
    rangeStart: number,
    rangeEnd: number,
    orderDirection?: OrderDirection,
    order?: StockSearchOrderOption
  ): Promise<StockCheckExtract[]> {
    setIsFetching(true);
    const term = searchInputTerm.trim();

    // define the search parameters.

    searchParams.RangeStart = rangeStart;
    searchParams.RangeEnd = rangeEnd;
    searchParams.OrderDirection = orderDirection ?? OrderDirection.Descending;
    searchParams.Order = order ?? StockSearchOrderOption.UpdatedDate;
    searchParams.SearchTerm = term;
    searchParams.Depot = depotId.toString();

    // define request information
    const requestOptions: RequestInit = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(searchParams),
    };

    // Send the call to the endpoint
    try {
      const response = await apiFetch(SEARCH_ENDPOINT, requestOptions);

      const rangeResponse = response as StockCheckExtract[];

      setIsFetching(false);
      rangeResponse.map((extract) => {
        const savedChecks = systemSavedAllChecks.filter(
          (slice: StockSliceSavedCheck) => {
            return (
              slice.stockCheckRef == extract.stockCheckExtractRef &&
              slice.companyId == currentCompanyId &&
              slice.depot == depotId &&
              slice.url == currentUrl
            );
          }
        );

        extract.hasSavedData = savedChecks.length > 0;
        return extract;
      });

      return rangeResponse;
    } catch (e) {
      console.log(e);
    }

    setIsFetching(false);

    return [];
  }

  function fetchMoreSearchResults(amount: number) {
    const response = fetchSearchResults(range.end, range.end + amount);
    setRange({ start: range.end, end: range.end + amount });

    return response;
  }

  function initialFetchSearchResults() {
    // intial fetch should be from range 0 - 10
    setRange({ start: 0, end: 10 });
    const response = fetchSearchResults(0, 10);
    response.then((e) => {
      setSearchResults(e);
    });
  }

  const changeSearchTerm = (event: { target: { value: string } }) => {
    const term = event.target.value;
    setSearchInputTerm(term);
  };

  const checkForReturn = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Go" || e.key === "Next") {
      const searchBar = document.getElementById(
        "stockcheck-input-search"
      ) as HTMLElement;
      searchBar.blur();
    }
  };

  const handleBackPress = () => {
    navigate("/");
  };

  const handleSavePress = () => {
    console.log("Handle Save Press not yet implemented");
  };

  const handleCompletePress = () => {
    console.log("Handle Complete Press not yet implemented");
  };

  const addStockCheckUser = (sce: StockCheckExtract) => {
    // unlock stock check
    apiFetch<StockCheckExtract>("/partsapp/stock/AddStockCheckUser", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(sce),
    }).finally(() => {
      navigate("/stockcheck/stockextract", {
        state: {
          sce: sce,
        },
      });
    });
  };

  useEffect(() => {
    // on load lifecycle event
    initialFetchSearchResults();
  }, []);

  const handleOnCancel = () => {
    setStockLargeWarningVisible(false);
    largeWarningStockCheckPlaceholder.current = null;
  };

  const handleOnContinue = () => {
    if (largeWarningStockCheckPlaceholder.current != null) {
      setStockLargeWarningVisible(false);
      addStockCheckUser(largeWarningStockCheckPlaceholder.current);
    }
  };

  return (
    <>
      <StockLargeWarning
        showLargeWarning={stockLargeWarningVisible}
        onCancel={handleOnCancel}
        onNavigate={handleOnContinue}
      />
      <div id="stockcheck-container" className="module-container">
        <div id="stock-nav-top" className="module-nav nav-top">
          <div id="stock-nav-top-container" className="module-nav-button top">
            <AbstractButton
              id="stockcheck-nav-back"
              text="Back"
              icon={<BackIcon className="module-nav-button-logo" />}
              activeColor={ForegroundColorSet.Blue}
              background={BackgroundColorSet.Black1}
              specialClass={null}
              disabled={false}
              actionCallback={handleBackPress}
            />
          </div>
          <div className="module-nav-button top">
            <AbstractButton
              id="stockextract-nav-sort-and-filter"
              text="Sort"
              icon={<SortIcon className="module-nav-button-logo" />}
              disabled={false}
              activeColor={
                searchAndFilterMenuVisible
                  ? ForegroundColorSet.White
                  : ForegroundColorSet.Blue
              }
              background={BackgroundColorSet.Black1}
              actionCallback={showSearchAndFilterMenu}
              specialClass={null}
            />
          </div>
          <div className="module-nav-button top">
            <AbstractButton
              id="stockcheck-nav-save"
              text="Save"
              icon={<SaveIcon className="module-nav-button-logo" />}
              activeColor={ForegroundColorSet.Blue}
              background={BackgroundColorSet.Black1}
              specialClass={null}
              disabled={true}
              actionCallback={handleSavePress}
            />
          </div>
          <div className="module-nav-button top">
            <AbstractButton
              id="stockcheck-nav-complete"
              text="Finish"
              icon={<CompleteIcon className="module-nav-button-logo" />}
              activeColor={ForegroundColorSet.Blue}
              background={BackgroundColorSet.Black1}
              specialClass={null}
              disabled={true}
              actionCallback={handleCompletePress}
            />
          </div>
        </div>
      </div>

      <div id="stockcheck-content" className="module-content">
        <ScrollableCardList
          fetchMoreResults={() => fetchMoreSearchResults(10)}
          renderItem={(sce) => (
            <StockCheckCard
              sce={sce}
              key={searchResults.indexOf(sce) + "-" + sce.stockCheckExtractRef}
              cardClicked={() => {
                if (sce.totalLines > 10000) {
                  largeWarningStockCheckPlaceholder.current = sce;
                  setStockLargeWarningVisible(true);
                } else {
                  addStockCheckUser(sce);
                }
              }}
            />
          )}
          initialItems={searchResults}
          isFetching={isFetching}
          loadsLazily={true}
        />
      </div>

      <div
        id="stockcheck-input-content"
        className="module-search stockcheck-search-input"
      >
        <input
          id="stockcheck-input-search"
          className="module-search-term-input-text"
          type="text"
          placeholder="&#x1F50D; Search..."
          autoCorrect="off"
          autoCapitalize="none"
          value={searchInputTerm}
          autoComplete="off"
          spellCheck="false"
          onBlur={() => initialFetchSearchResults()}
          onChange={changeSearchTerm}
          onKeyDown={checkForReturn}
        />
      </div>

      <div
        id="stockcheck-search-input-curtain"
        className="search-bar-safe-area-curtain"
      />

      <NavBar activePage={"check"} />

      {SearchAndFilterClasses == "modal-open" ? (
        <div id="sort-filter-modal-mask" className="modal-mask"></div>
      ) : null}
      <StockSortBy
        onShowTrigger={showSearchAndFilterMenuTrigger}
        onOkayCallback={updateSort}
        onCancelCallback={cancelFilterButtonClicked}
        visibilityClassName={SearchAndFilterClasses}
        searchData={defaultStockCheckExtractSearch}
      />
    </>
  );
};

export default StockCheck;
